import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, Crown } from "lucide-react";

const PRO_BENEFITS = [
    "Unlimited products",
    "Product photos",
    "Public business website",
    "Shareable Bizlinkk link",
    "QR code",
    "Basic analytics",
    "AI content editing",
    "Hosting & SSL",
];

export default function UpgradeModal({ open, onOpenChange }) {
    const [loading, setLoading] = useState(false);

    const startPro = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/my/subscription/upgrade");
            toast.success(data?.message || "Your Pro upgrade request is noted.");
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md glass-strong border-border rounded-3xl p-0 overflow-hidden" data-testid="upgrade-modal">
                <div className="p-6 sm:p-7">
                    <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-semibold">
                        <Crown className="w-3.5 h-3.5" /> Bizlinkk Pro
                    </div>
                    <h2 className="font-heading text-2xl font-semibold mt-4 leading-tight">
                        Your 5 free products are already added.
                    </h2>
                    <p className="mt-2 text-muted-foreground text-sm">
                        Want to showcase more of your products? Upgrade to Bizlinkk Pro for unlimited products.
                    </p>

                    <div className="mt-5 rounded-2xl bg-secondary/50 p-4">
                        <div className="flex items-end gap-1.5">
                            <span className="font-heading text-3xl font-semibold">₹99</span>
                            <span className="text-muted-foreground mb-1 text-sm">for your first month</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Then ₹199/month</div>
                        <ul className="mt-4 grid grid-cols-2 gap-y-2 gap-x-3">
                            {PRO_BENEFITS.map((b) => (
                                <li key={b} className="flex items-start gap-1.5 text-[13px]">
                                    <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-6 flex flex-col gap-2">
                        <Button onClick={startPro} disabled={loading} className="btn-accent rounded-full h-12 text-base w-full" data-testid="upgrade-start-pro-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Start Pro for ₹99</>}
                        </Button>
                        <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full w-full text-muted-foreground" data-testid="upgrade-maybe-later-btn">
                            Maybe later
                        </Button>
                    </div>
                    <p className="mt-3 text-[11px] text-center text-muted-foreground">
                        Online payment is being connected — we'll activate Pro as soon as it's live. You won't be charged yet.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
